/**
 * Phase 18.17 Batch 5 — Hubs live list (CRM-managed).
 *
 * Replaces the hard-coded /app/frontend/src/data/hubs.js list with a
 * live render from `/api/website/locations`. Each row shows:
 *   - country flag (via country_code → unicode emoji)
 *   - country name + hub label
 *   - address, contact person, phone, e-mail
 *   - "Contact" CTA → mailto with subject prefilled
 *
 * Inactive hubs are automatically filtered server-side; the row order
 * matches the `sort_order` field. No flag → small MapPin fallback.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, User, ArrowRight, Loader2 } from "lucide-react";
import { countryCodeToFlag } from "../utils/countryFlag";
import { useI18n } from "../context/I18nContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function HubsLiveList() {
  const { t } = useI18n();
  const [hubs, setHubs] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/website/locations`, { timeout: 10000 })
      .then((r) => {
        if (cancelled) return;
        const list = Array.isArray(r.data) ? r.data : [];
        setHubs(list.filter((h) => h.active !== false));
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return (
      <section className="py-12 bg-[#F8F9FA]" data-testid="hubs-live-list-error">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-gray-600">Onze hubs zijn momenteel niet beschikbaar — bel ons gerust op <a href="tel:+31488235999" className="text-[#1a5193] font-semibold">+31 (0) 488 23 5999</a>.</p>
        </div>
      </section>
    );
  }

  if (hubs === null) {
    return (
      <section className="py-16 bg-[#F8F9FA]" data-testid="hubs-live-list-loading">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center gap-2 text-gray-500 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Hubs laden…
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-[#F8F9FA]" data-testid="hubs-live-list">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {hubs.map((h, idx) => (
            <HubCard key={h.id || idx} hub={h} idx={idx} />
          ))}
        </div>
        {hubs.length === 0 && (
          <p className="text-center text-gray-500 text-sm" data-testid="hubs-empty">
            Nog geen hubs gepubliceerd.
          </p>
        )}
      </div>
    </section>
  );
}

function HubCard({ hub, idx }) {
  const flag = countryCodeToFlag(hub.country_code);
  const subject = encodeURIComponent(`Aanvraag ${hub.country} hub — 5999Cargo`);
  const mailto = hub.email ? `mailto:${hub.email}?subject=${subject}` : "/contact?get-quote=1#quote-form";
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ delay: idx * 0.04 }}
      className="bg-white border border-gray-200 p-6 flex flex-col h-full hover:border-[#1a5193]/40 transition-colors"
      data-testid={`hub-card-${(hub.country_code || "xx").toLowerCase()}-${idx}`}
    >
      {hub.hub_label ? (
        <span className="inline-block text-[10px] tracking-[0.2em] uppercase text-[#1a5193] font-chivo font-bold mb-3">
          {hub.hub_label}
        </span>
      ) : null}
      <div className="flex items-center gap-2.5">
        {flag ? (
          <span className="text-2xl leading-none" aria-hidden="true" data-testid={`hub-flag-${(hub.country_code || "").toLowerCase()}`}>
            {flag}
          </span>
        ) : (
          <MapPin className="w-5 h-5 text-[#1a5193]" />
        )}
        <h3 className="font-chivo font-black text-xl text-[#082c50] leading-tight">{hub.country}</h3>
      </div>
      <div className="mt-4 space-y-3 text-sm text-gray-700 flex-1">
        {hub.address ? (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
            <div className="leading-relaxed whitespace-pre-line">{hub.address}</div>
          </div>
        ) : null}
        {hub.contact_person ? (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-[#1a5193] flex-shrink-0" />
            <span>{hub.contact_person}</span>
          </div>
        ) : null}
        {hub.phone ? (
          <div className="flex items-start gap-2">
            <Phone className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
            <a
              href={`tel:${String(hub.phone).replace(/\s+/g, "")}`}
              className="hover:text-[#1a5193]"
            >
              {hub.phone}
            </a>
          </div>
        ) : null}
        {hub.email ? (
          <div className="flex items-start gap-2">
            <Mail className="w-4 h-4 text-[#1a5193] mt-0.5 flex-shrink-0" />
            <a href={`mailto:${hub.email}`} className="break-all hover:text-[#1a5193]">
              {hub.email}
            </a>
          </div>
        ) : null}
      </div>
      <a
        href={mailto}
        className="mt-5 inline-flex items-center justify-between bg-[#082c50] hover:bg-[#0a335c] text-white font-chivo font-bold tracking-wide uppercase text-xs px-4 py-3 transition-colors"
        data-testid={`hub-contact-${(hub.country_code || "xx").toLowerCase()}-${idx}`}
      >
        Contact deze hub
        <ArrowRight className="w-3.5 h-3.5 ml-2" />
      </a>
    </motion.div>
  );
}
